import React from 'react';
import { money } from '../../lib/money';
import { Badge } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { poStatusMeta, deliveryStatusMeta } from './purchaseOrdersApi';

/**
 * A rich purchase-order picker list for "record a receipt" / "start a return" drawers.
 * Each card answers the questions a store keeper actually has: which order, from which
 * supplier and property, raised by whom, from which requisition, due when, and how much
 * of it has already arrived.
 */
export default function PoPickList({ items, onPick, showDelivery = true }) {
  const t = useT();
  const { properties } = useProperty();
  const propertyName = (id) => (id ? properties?.find((p) => p.id === id)?.name : null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="rcv-po-picker">
      {items.map((po) => {
        const m = poStatusMeta(po.status);
        const dm = showDelivery ? deliveryStatusMeta(po.deliveryStatus) : null;
        const prop = propertyName(po.propertyId);

        const expected = po.expectedDate ? new Date(po.expectedDate) : null;
        const daysLate = expected
          ? Math.floor((today - new Date(expected.getFullYear(), expected.getMonth(), expected.getDate())) / 86400000)
          : 0;
        const overdue = expected && daysLate > 0 && po.deliveryStatus !== 'Delivered' && po.status !== 'Closed';

        // Only when a line is fully in — the "Partially received" badge covers the in-between case.
        const showProgress = po.lineCount > 0 && (po.receivedLineCount ?? 0) > 0;

        return (
          <button type="button" key={po.id} className="rcv-po-pick" onClick={() => onPick(po)}>
            <div className="rcv-po-pick__body">
              <div className="rcv-po-pick__head">
                <span className="rcv-po-pick__num">{po.number}</span>
                <Badge tone={m.tone} dot>{t(m.label)}</Badge>
                {dm && <Badge tone={dm.tone}>{t(dm.label)}</Badge>}
                {overdue && <Badge tone="danger">{t('{n} day(s) late', { n: daysLate })}</Badge>}
              </div>

              <div className="rcv-po-pick__sub">
                <span className="rcv-po-pick__supplier">{po.supplierName || t('No supplier')}</span>
                {prop && (
                  <span className="rcv-po-pick__meta-item">
                    <Icon.Building size={12} /> {prop}
                  </span>
                )}
              </div>

              <div className="rcv-po-pick__meta">
                {po.createdByName && (
                  <span className="rcv-po-pick__meta-item">
                    <Icon.User size={12} /> {po.createdByName}
                  </span>
                )}
                {po.sourceRequisitionNumber && (
                  <span className="rcv-po-pick__meta-item">
                    <Icon.ClipboardList size={12} /> {t('From {number}', { number: po.sourceRequisitionNumber })}
                  </span>
                )}
                {expected && (
                  <span className={`rcv-po-pick__meta-item${overdue ? ' rcv-po-pick__meta-item--danger' : ''}`}>
                    <Icon.Calendar size={12} /> {t('expected {date}', { date: expected.toLocaleDateString() })}
                  </span>
                )}
                {showProgress && (
                  <span className="rcv-po-pick__meta-item">
                    <Icon.ArrowDownCircle size={12} /> {t('{received}/{total} lines received', { received: po.receivedLineCount, total: po.lineCount })}
                  </span>
                )}
              </div>
            </div>

            <div className="rcv-po-pick__side">
              <span className="rcv-po-pick__total">{money(po.total)}</span>
              <span className="rcv-po-pick__lines">{t('{n} line(s)', { n: po.lineCount })}</span>
              <Icon.ChevronRight size={16} className="rcv-po-pick__chev" />
            </div>
          </button>
        );
      })}
    </div>
  );
}
